<script lang="ts" setup>
// ===================================================================
// Imports
// ===================================================================
import { ref, computed, inject, watch } from "vue"
import * as yup from "yup"

// Composables
import { useForm } from "vee-validate"
import { useI18n } from "vue-i18n"
import { useIrveStore } from "@modules/irve/stores/irve.store"
import { useAuthStore } from "@core/stores/auth.store"
import { useFetchWrapper } from "@core/composables"
import { IrveFoisonnement, IrveFormProfileRow } from "@types"
import type { Services } from "@services"
import { watchOnce } from "@vueuse/core"
import { DAYS } from "@modules/irve/constants"
import { useToast } from "primevue/usetoast"
import { groupSimilarBornes } from "@modules/irve/helpers"

// Composants
import PanelBorne from "@modules/irve/components/form/panel/PanelBorne.vue"
import PanelInstallation from "@modules/irve/components/form/panel/PanelInstallation.vue"
import PanelBuilding from "@modules/irve/components/form/panel/PanelBuilding.vue"
import PanelUtilisationProfile from "@modules/irve/components/form/panel/PanelUtilisationProfile.vue"

// ===================================================================
// Initialisation des services
// ===================================================================
const { $createAnalysisResidualPower } = inject<Services>("services")!
const irveStore = useIrveStore()

// ===================================================================
// Gestion du formulaire
// ===================================================================

const schema = yup.object({
    dateTo: yup
        .date()
        .required("La date de fin de période simulée est requise."),
    usage: yup.string().required("L'usage est requis."),
    name: yup.string(),
    installationType: yup
        .string()
        .required("Le type d'installation est requis."),
    source: yup.string().required("La source est requises"),
    utilisationProfile: yup
        .array()
        .min(1, "Veuillez renseigner au minimum un profil d'utilisation")
        .required(),
    bornes: yup
        .array()
        .of(
            yup.object().shape({
                power: yup.number().min(0).required(),
                powerFactor: yup.number().min(0).max(1).required(),
                powerMean: yup.number().min(0).required(),
                quantity: yup
                    .number()
                    .min(
                        1,
                        "Veuillez rensiegner au minimum une borne installée"
                    )
                    .required(),
            })
        )
        .required("Les bornes sont requises")
        .min(
            1,
            "Aucune borne n'a été configurée, veuillez saisir au minimum une borne."
        ),
})

const { errors, handleSubmit, defineField, validateField, setFieldError } =
    useForm<{
        dateTo: Date
        usage: string
        name: string
        installationType: string
        utilisationProfile: any[]
        bornes: any[]
        source: string
    }>({
        validationSchema: schema,
        initialValues: {
            dateTo: undefined,
            usage: irveStore.building?.infos?.occupation?.usage || "",
            installationType: "",
            utilisationProfile: [],
            name: "",
            bornes: [
                {
                    power: null,
                    powerFactor: null,
                    powerMean: null,
                    quantity: 1,
                },
            ],
            source:
                irveStore.getListPdlForResidualPowerAnalysis?.[0]?._id_source ??
                "",
        },
    })
const [dateTo] = defineField("dateTo")
const [installationType] = defineField("installationType")
const [usage] = defineField("usage")
const [name] = defineField("name")
const [source] = defineField("source")
const [utilisationProfile] = defineField("utilisationProfile")
const [bornes, bornesAttrs] = defineField("bornes", {
    props: (state) => {
        return {
            errors: state.errors, // here is how I do it
        }
    },
})

const onSubmit = handleSubmit(onSuccess, onInvalidSubmit)

/**
 * Appelé lors de la soumission du formulaire
 */
function onSuccess() {
    if (fetchCreateAnalysis.isFetching.value) {
        return
    }
    fetchCreateAnalysis.fetch()
}

/**
 * Appelé lors de la soumission du formulaire avec des erreurs
 */
function onInvalidSubmit() {
    if (!panels.value) return
    // trigger les validations des composants enfants
    panels.value.forEach(async (panel) => {
        if (!panel?.validate) return
        await panel.validate()
    })
}
const toast = useToast()
/**
 * Appel de l'API pour créer une analyse de puissance résiduelle
 */
const fetchCreateAnalysis = useFetchWrapper(
    async () => {
        toast.removeGroup("fetchCurrentAnalysis")
        const authStore = useAuthStore()

        const _id_source = source.value
        const _id_company = authStore.companyId
        const _id_user = authStore.userId
        const _id_building = irveStore.building?._id as string

        // La date de fin de la période simulée est fixée à -364 jours à partir de la date de fin
        const dateFrom = new Date(dateTo.value)
        dateFrom.setDate(dateFrom.getDate() - 364)

        // Appel de l'API pour créer une analyse de puissance résiduelle
        const data = await $createAnalysisResidualPower({
            _id_source,
            _id_company,
            _id_user,
            _id_building,
            dateTo: dateTo.value,
            dateFrom,
            bornes: bornes.value,
            name: name.value,
            utilisationProfile: utilisationProfile.value,
            installation_type: installationType.value,
            usage: usage.value,
        })
        // Affichage d'un toast pour informer l'utilisateur que l'analyse est en cours
        toast.add({
            severity: "success",
            summary: "Analyse réalisée !",
            detail: "Chargement des données en cours...",
            life: 3000,
            group: "fetchCurrentAnalysis",
        })

        // Réinitialisation des valeurs du formulaire
        name.value = ""

        // Ajout dans la liste des analyses du store
        if (!irveStore.analyses?.length) {
            irveStore.analyses = []
        }
        irveStore.analyses?.push(data)

        // Mise à jour de l'analyse en cours
        irveStore.currentAnalysisId = data?._id_analysis
    },
    (error) => {
        toast.add({
            severity: "error",
            summary: "Erreur",
            detail: error?.message,
            life: 3000,
            group: "fetchCurrentAnalysis",
        })
    }
)

/**
 * Initialise les valeurs du formulaire à partir de l'analyse en cours
 */
function initialiseValuesFromStore() {
    if (!irveStore.getCurrentAnalysis) return

    // Modifie les valeurs initiales si déjà une analyse existante pour la préremplir avec les informations
    dateTo.value = new Date(irveStore.getCurrentAnalysis.inputs.date_to)
    if (irveStore.getCurrentAnalysis.inputs.usage) {
        usage.value = irveStore.getCurrentAnalysis.inputs.usage
    } else {
        usage.value = irveStore.building?.infos?.occupation?.usage || ""
    }
    if (irveStore.getCurrentAnalysis.inputs.installation_type) {
        installationType.value =
            irveStore.getCurrentAnalysis.inputs.installation_type
    }
    bornes.value = groupSimilarBornes(
        irveStore.getCurrentAnalysis.inputs.bornes
    )
    source.value = irveStore.getCurrentAnalysis.inputs._id_source

    // Création du tableau de profil d'utilisation à partir du tableau des foisonnements
    utilisationProfile.value = convertFoisonnementToProfile(
        irveStore.getCurrentAnalysis.inputs.foisonnement
    )
}

/**
 * Permet de convertir un tableau de foisonnement en un profil d'utilisation
 * @param foisonnementArray
 */
function convertFoisonnementToProfile(
    foisonnementArray: IrveFoisonnement[]
): IrveFormProfileRow[] {
    const profile: IrveFormProfileRow[] = []

    // Construire le profil d'utilisation à partir du tableau de foisonnement
    foisonnementArray.forEach(
        ({ day_fr, hour_fr, coefficient_foisonnement }) => {
            if (!coefficient_foisonnement) return

            const day = DAYS[day_fr]

            let profileRow = profile.find((row) =>
                row.days.includes(day.toLowerCase())
            )

            if (!profileRow) {
                profileRow = {
                    days: [day.toLowerCase()],
                    periods: [{ start: hour_fr, end: hour_fr + 1 }],
                    foisonnement: coefficient_foisonnement,
                }
                profile.push(profileRow)
            } else {
                const lastPeriod =
                    profileRow.periods[profileRow.periods.length - 1]

                if (
                    lastPeriod &&
                    lastPeriod.end === hour_fr &&
                    profileRow.foisonnement === coefficient_foisonnement
                ) {
                    lastPeriod.end = hour_fr + 1
                } else {
                    profileRow.periods.push({
                        start: hour_fr,
                        end: hour_fr + 1,
                    })
                    profileRow.foisonnement = coefficient_foisonnement
                }
            }
        }
    )

    // Filtrer les profils sans périodes
    const list = profile.filter((row) => row.periods.length > 0)

    // Modifie les jours complets qui sont notés 24 et 0 en 0 et 0
    list.forEach((row) => {
        row.periods = row.periods.map((period) => {
            if (
                (period.start === 24 && period.end === 0) ||
                (period.start === 0 && period.end === 24)
            ) {
                return { start: 0, end: 0 }
            }
            return period
        })
    })

    return list
}

watchOnce(
    () => irveStore.getCurrentAnalysis,
    () => {
        initialiseValuesFromStore()
    }
)

watch(
    () => irveStore.fetchCurrentAnalysis.isFetching,
    (value) => {
        if (!value) {
            toast.removeGroup("fetchCurrentAnalysis")
        }
    }
)

//============================================================================= //
// Panels
//============================================================================= //
/**
 * Valeur de l'identifiant du panel actuellement visible par l'utilisateur
 */
const value = ref<string>("profile")
/**
 * Liste des références des panels pour les valider depuis le parent
 */
const panels = ref<(HTMLElement & { validate: () => Promise<boolean> })[]>([])
/**
 * Référence du composant parent pour accéder aux panels
 */
const visitedPanels = ref<Record<string, boolean>>({})

/**
 * Récupère l'index du panel dans la liste des panels à partir de l'identfiant du panel
 * @param panelId identifiant du panel
 */
const getPanelIndex = (panelId: string): number => {
    return getPanelList.value.findIndex((panel) => panel.value === panelId)
}

watch(value, async (newValue, oldValue) => {
    const oldPanelId = getPanelIndex(oldValue)
    const newPanelId = getPanelIndex(newValue)
    if (newPanelId !== -1) {
        visitedPanels.value[getPanelList.value[newPanelId].value] = true
    }
    if (oldValue) {
        try {
            if (oldValue == "buildings") {
                await validateField("usage")
                await validateField("installationType")
            } else {
                await validateField(oldValue as any)
            }
            const panel = panels.value[oldPanelId]
            if (!panels.value) return
            if (!panel?.validate) return
            await panel.validate()
        } catch (err) {
            console.error(`Erreur de validation pour ${oldValue} :`, err)
        }
    }
})

/**
 * Calcul l'icone du panel en fonction de sa validation
 * @param panelId - identifiant du panel
 * @param isCorrect - si le panel est correct
 */
function computedValidationPanelIcon(panelId: string, isCorrect: boolean) {
    if (!visitedPanels.value[panelId]) {
        return "pi pi-check text-gray-400"
    }
    return isCorrect
        ? "pi pi-check text-slate-400 "
        : "pi pi-check text-cyan-400"
}
/**
 * Liste des panels avec leurs composants et leurs propriétés
 */
const getPanelList = computed(() => {
    return [
        {
            title: t(`header.bornes`),
            value: "bornes",
            component: PanelBorne,
            props: {
                bornes: bornes.value,
            },
            on: {
                "update:bornes": ($event: any) => {
                    bornes.value = $event
                },
            },
            showError: false,
            errors: Object.values(bornesAttrs.value.errors),
        },
        {
            title: t(`header.profile`),
            value: "profile",
            component: PanelUtilisationProfile,
            props: {
                utilisationProfile: utilisationProfile.value,
            },
            on: {
                "update:utilisation-profile": async ($event: any) => {
                    utilisationProfile.value = $event
                    const r = await validateField("utilisationProfile")
                    setFieldError("utilisationProfile", r.errors)
                },
            },
            showError: true,
            errors: Object.entries(errors.value)
                .filter(([key]) => ["utilisationProfile"].includes(key))
                .map(([, value]) => value),
        },
        {
            title: t(`header.installations`),
            value: "installations",
            component: PanelInstallation,
            props: {
                source: source.value,
            },
            on: {
                "update:source": ($event: any) => (source.value = $event),
            },
            showError: false,
            errors: Object.entries(errors.value)
                .filter(([key]) => ["source"].includes(key))
                .map(([, value]) => value),
        },
        {
            title: t(`header.buildings`),
            value: "buildings",
            component: PanelBuilding,
            props: {
                usage: usage.value,
                installationType: installationType.value,
            },
            on: {
                "update:usage": ($event: any) => (usage.value = $event),
                "update:installationType": ($event: any) =>
                    (installationType.value = $event),
            },
            showError: false,
            errors: Object.entries(errors.value)
                .filter(([key]) => ["usage", "installationType"].includes(key))
                .map(([, value]) => value),
        },
    ]
})

/**================================================================*/
/*                         Lang
/*==================================================================*/
const { t } = useI18n({
    messages: {
        fr: {
            button: {
                label: "Lancer l'analyse",
            },
            name: "Nom de l'analyse",
            date_from: {
                label: "Date de fin de la période simulée",
                tooltip:
                    "L'analyse est faite sur un an de données. Renseignez la date de fin de la simulation, de préférence antérieure à la date de dernière donnée du compteur électrique.",
            },
            title: "Paramètres",
            header: {
                profile: "Profil d'utilisation",
                bornes: "Bornes",
                installations: "Installation",
                buildings: "Batiments",
            },
            error: "Veuillez compléter tous les champs ci-dessous pour lancer l'analyse.",
        },
    },
})
</script>

<template>
    <form
        class="irve-form flex flex-col gap-6 rounded-md border border-[var(--surface-300,#e0e0e0)] bg-white p-6"
        @submit.prevent.stop="onSubmit"
    >
        <header
            class="flex flex-wrap flex-row gap-4 items-center justify-between"
        >
            <div class="flex flex-row items-center gap-4">
                <h1 class="font-bold text-xl text-primary-700">
                    {{ t("title") }}
                </h1>
                <span class="text-cyan-400">&#9679;</span>
                <BaseInplace>
                    <template #display>
                        <p>
                            {{ name ? name : t("name") }}
                            <i class="pi pi-pencil ml-2 text-cyan-400"></i>
                        </p>
                    </template>
                    <template #content="{ closeCallback }">
                        <span class="inline-flex items-center gap-2">
                            <BaseInputText v-model="name" autofocus />
                            <BaseButton
                                icon="pi pi-check"
                                text
                                severity="success"
                                @click="closeCallback"
                            />
                            <BaseButton
                                icon="pi pi-times"
                                text
                                severity="danger"
                                @click="closeCallback"
                            />
                        </span>
                    </template>
                </BaseInplace>
            </div>
            <BaseSkeleton
                v-if="irveStore.fetchAnalyses.isFetching"
                width="20rem"
                height="3rem"
            ></BaseSkeleton>
            <div
                v-if="!irveStore.fetchAnalyses.isFetching"
                class="flex flex-wrap flex-row items-center gap-4"
            >
                <BaseInputWrapper
                    class="w-auto min-w-[21rem]"
                    input-id="date-from"
                    :label="
                        errors.dateTo
                            ? `&#9432; ` + t('date_from.label')
                            : t('date_from.label')
                    "
                    v-tooltip.bottom="{
                        value: t('date_from.tooltip'),
                    }"
                >
                    <BaseDatePicker
                        v-model="dateTo"
                        :invalid="errors.dateTo"
                        date-format="dd/mm/yy"
                        :max-date="new Date()"
                        v-mask="'99/99/9999'"
                    />
                    <template #prefix>
                        <BaseInputGroupAddon>
                            <i class="pi pi-calendar text-xs"></i>
                        </BaseInputGroupAddon>
                    </template>
                </BaseInputWrapper>
                <!-- à faire lors de la gestion des erreurs et lorsque j'aurais la route-->
                <BaseButton
                    type="submit"
                    :label="t('button.label')"
                    icon="pi pi-check"
                    icon-pos="right"
                    :loading="fetchCreateAnalysis.isFetching.value"
                ></BaseButton>
            </div>
        </header>
        <BaseTabs
            class="flex flex-col gap-2.5"
            v-model:value="value"
            v-show="!irveStore.fetchAnalyses.isFetching"
        >
            <BaseTabList
                v-model="value"
                class="w-full [&_.p-tablist-content]:w-full [&_.p-tablist-tab-list]:grid [&_.p-tablist-tab-list]:grid-cols-4"
            >
                <BaseTab
                    :value="panel.value"
                    v-for="panel in getPanelList"
                    :key="panel.value"
                    class="relative flex flex-row items-center justify-center gap-2"
                >
                    <transition
                        appear
                        enter-active-class="transition-all duration-300 ease-out"
                        enter-from-class="opacity-0 scale-50"
                        enter-to-class="opacity-100 scale-100"
                        leave-active-class="transition-all duration-200 ease-in"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0 scale-50"
                    >
                        <BaseBadge
                            class="absolute right-[5%] top-[5%] z-10"
                            v-if="panel.errors.length > 0"
                            :value="panel.errors.length"
                            size="small"
                            severity="danger"
                        />
                    </transition>
                    <p>
                        {{ panel.title }}
                    </p>
                    <i
                        :class="
                            computedValidationPanelIcon(
                                panel.value,
                                panel.errors.length > 0
                            )
                        "
                    ></i>
                </BaseTab>
            </BaseTabList>
            <BaseTabPanels>
                <BaseTabPanel
                    v-for="panel in getPanelList"
                    :key="panel.value"
                    :value="panel.value"
                >
                    <component
                        ref="panels"
                        :is="panel.component"
                        v-bind="panel.props"
                        v-on="panel.on"
                    >
                        <template #error>
                            <BaseMessage
                                class="mt-6"
                                v-if="panel.showError && panel.errors.length"
                                severity="error"
                            >
                                {{ panel.errors.join("\n") }}</BaseMessage
                            >
                        </template>
                    </component>
                </BaseTabPanel>
            </BaseTabPanels>
        </BaseTabs>
        <div
            v-if="irveStore.fetchAnalyses.isFetching"
            class="flex flex-col gap-6"
        >
            <div class="flex gap-6">
                <BaseSkeleton
                    v-for="i of 4"
                    :key="i"
                    width="25%"
                    height="5rem"
                ></BaseSkeleton>
            </div>
            <BaseSkeleton width="100%" height="20rem"></BaseSkeleton>
        </div>
        <BaseToast group="fetchCurrentAnalysis" />
    </form>
</template>
